import { useRef } from "react"

import { Box, Stack, Typography } from "@mui/material"

import type { LinkType } from "../../graphql/types"
import { Breadcrumbs, Button, Image, type FC, type FCWC, type ResponsiveImageType } from ".."
import HeroMedia from "./HeroMedia"

type HeroProps = {
  title: string

  subtitle?: string
  subtitleImageUrl?: string
  cta?: LinkType
  media: ResponsiveImageType[]
  video?: string
  path?: LinkType[]
  travelInformation?: string | null
  isTripIdea?: boolean
  reducedHeight?: boolean
}

const extractSpanContent = (htmlString: string) => {
  if (!htmlString) {
    return { nonSpanContent: "", spanContent: "" }
  }

  const spanMatch = htmlString.match(/<span[^>]*>(.*?)<\/span>/)
  const spanContent = spanMatch ? spanMatch[1] : ""
  const nonSpanContent = htmlString.replace(/<span[^>]*>.*?<\/span>/, "")

  return { nonSpanContent, spanContent }
}

const Hero: FC<HeroProps> = ({
  title,
  subtitle,
  subtitleImageUrl,
  cta,
  media,
  video,
  path,
  travelInformation = null,
  isTripIdea = false,
  reducedHeight = false,
}) => {
  const carouselRef = useRef<HTMLDivElement>(null)
  const { nonSpanContent, spanContent } = extractSpanContent(title)

  const scrollToSection = () => {
    const element = document.getElementById("learn-more")
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
      // Perform a second scroll after a short delay
      setTimeout(() => {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }, 200)
    }
  }
  const scrollToTrips = () => {
    const element = document.getElementById("suggested-trips-header")
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      })
      // Perform a second scroll after a short delay
      setTimeout(() => {
        element.scrollIntoView({
          behavior: "smooth",
          block: "start",
        })
      }, 200)
    }
  }
  return (
    <HeroContainer reducedHeight={reducedHeight}>
      {reducedHeight ? (
        <HeroInner>
          <HeroMedia video={video} media={media} containerRef={carouselRef} />
          <HeroContent>
            {path && path.length > 0 && <Breadcrumbs items={path} />}
            {subtitle && !isTripIdea && (
              <Box position="relative" zIndex={5} textAlign="center">
                {subtitleImageUrl && (
                  <Image
                    src={subtitleImageUrl}
                    alt={title}
                    position="absolute"
                    top="50%"
                    left="50%"
                    sx={{ transform: "translate(-50%, -50%)", pointerEvents: "none" }}
                    loading="eager"
                  />
                )}
                <Typography
                  variant="body.lg"
                  component="h3"
                  color={(theme) => theme.palette["neutral.100"].main}
                  textAlign="center"
                >
                  {subtitle}
                </Typography>
              </Box>
            )}
            {isTripIdea ? (
              <TripIdeaHeroHeading title={title} subtitle={subtitle!} />
            ) : (
              <Box position="relative" zIndex={5}>
                <Typography
                  variant="heading.3xl"
                  component="h1"
                  color={(theme) => theme.palette["neutral.100"].main}
                  textAlign="center"
                >
                  {nonSpanContent}
                  {spanContent && !travelInformation && (
                    <Typography
                      variant="heading.xl"
                      component="span"
                      color={(theme) => theme.palette["neutral.100"].main}
                      style={{ display: "block" }}
                    >
                      {spanContent}
                    </Typography>
                  )}
                  {travelInformation && (
                    <Typography
                      variant="heading.xl"
                      component="span"
                      color={(theme) => theme.palette["neutral.100"].main}
                      style={{ display: "block" }}
                    >
                      {travelInformation}
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}
            <Stack direction={"row"} gap={10}>
              {cta && (
                <Box display={path?.[3] ? "none" : "block"} className={`btn-${path?.[3]?.title}-1`}>
                  <Button
                    variant="secondary"
                    onClick={scrollToTrips}
                    sx={
                      path?.[3]
                        ? {
                            "backgroundColor": "#F05B2A ",
                            "boxShadow": "none",
                            "color": "#FFFFFF ",
                            "transition": "background-color 0.3s ease, box-shadow 0.3s ease",
                            "&:hover": {
                              backgroundColor: "#D04F23", // A slightly darker shade on hover
                              boxShadow: "none",
                            },
                            "zIndex": 5,
                          }
                        : { "boxShadow": 0, "zIndex": 5, "&:hover": { boxShadow: 0 } }
                    }
                  >
                    {path?.[3] ? "Find A Trip" : cta.title}
                  </Button>
                </Box>
              )}
              {path?.[3] && (
                <Box display={path?.[3] ? "none" : "block"} className={`btn-${path?.[3].title}`}>
                  <Button
                    variant="secondary"
                    onClick={scrollToSection}
                    sx={{
                      "boxShadow": 0,
                      "zIndex": 5,
                      "&:hover": { boxShadow: 0 },
                      "width": 158,
                      "padding": "0 !important",
                    }}
                  >
                    {`Discover ${path?.[3].title}`}
                  </Button>
                </Box>
              )}
            </Stack>
          </HeroContent>
          {media?.length > 0 && (
            <Box position="absolute" top="0" left="0" bottom="0" right="0" ref={carouselRef} />
          )}
        </HeroInner>
      ) : (
        <>
          <HeroMedia video={video} media={media} containerRef={carouselRef} />
          <HeroContent>
            {path && path.length > 0 && <Breadcrumbs items={path} />}
            {subtitle && !isTripIdea && (
              <Box position="relative" zIndex={5} textAlign="center">
                {subtitleImageUrl && (
                  <Image
                    src={subtitleImageUrl}
                    alt={title}
                    position="absolute"
                    top="50%"
                    left="50%"
                    sx={{ transform: "translate(-50%, -50%)", pointerEvents: "none" }}
                    loading="eager"
                  />
                )}
                <Typography
                  variant="body.lg"
                  component="h3"
                  color={(theme) => theme.palette["neutral.100"].main}
                  textAlign="center"
                >
                  {subtitle}
                </Typography>
              </Box>
            )}
            {isTripIdea ? (
              <TripIdeaHeroHeading title={title} subtitle={subtitle!} />
            ) : (
              <Box position="relative" zIndex={5} maxWidth={{ xs: "100%" }}>
                <Typography
                  variant="heading.3xl"
                  component="h1"
                  color={(theme) => theme.palette["neutral.100"].main}
                  textAlign="center"
                  sx={{
                    maxWidth: {xs: "100%"},
                    wordWrap: {xs: "break-word"},
                  }}
                >
                  {nonSpanContent}
                  {spanContent && !travelInformation && (
                    <Typography
                      variant="heading.xl"
                      component="span"
                      color={(theme) => theme.palette["neutral.100"].main}
                      style={{ display: "block" }}
                    >
                      {spanContent}
                    </Typography>
                  )}
                  {travelInformation && (
                    <Typography
                      variant="heading.xl"
                      component="span"
                      color={(theme) => theme.palette["neutral.100"].main}
                      style={{ display: "block" }}
                    >
                      {travelInformation}
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}
            <Stack direction={"row"} gap={10}>
              {cta && (
                <Box display={path?.[3] ? "none" : "block"} className={`btn-${path?.[3]?.title}-1`}>
                  <Button
                    variant="secondary"
                    onClick={scrollToTrips}
                    sx={
                      path?.[3]
                        ? {
                            "backgroundColor": "#F05B2A ",
                            "boxShadow": "none",
                            "color": "#FFFFFF ",
                            "transition": "background-color 0.3s ease, box-shadow 0.3s ease",
                            "&:hover": {
                              backgroundColor: "#D04F23", // A slightly darker shade on hover
                              boxShadow: "none",
                            },
                            "zIndex": 5,
                          }
                        : { "boxShadow": 0, "zIndex": 5, "&:hover": { boxShadow: 0 } }
                    }
                  >
                    {path?.[3] ? "Find A Trip" : cta.title}
                  </Button>
                </Box>
              )}
              {path?.[3] && (
                <Box display={path?.[3] ? "none" : "block"} className={`btn-${path?.[3].title}`}>
                  <Button
                    variant="secondary"
                    onClick={scrollToSection}
                    sx={{
                      "boxShadow": 0,
                      "zIndex": 5,
                      "&:hover": { boxShadow: 0 },
                      "width": 158,
                      "padding": "0 !important",
                    }}
                  >
                    {`Discover ${path?.[3].title}`}
                  </Button>
                </Box>
              )}
            </Stack>
          </HeroContent>
          {media?.length > 0 && (
            <Box position="absolute" top="0" left="0" bottom="0" right="0" ref={carouselRef} />
          )}
        </>
      )}
    </HeroContainer>
  )
}

export default Hero

/** A sub-component for the section container of the hero content */
const HeroContainer: FCWC<{ reducedHeight?: boolean }> = ({
  children,
  reducedHeight,
  ...props
}) => (
  <Stack
    component="header"
    alignItems="stretch"
    justifyContent="flex-end"
    width="100%"
    position="relative"
    top="0"
    height={{ xs: reducedHeight ? "auto" : 700, md: reducedHeight ? "auto" : "100vh" }}
    pt={{ xs: 16, md: 32.5 }}
    sx={{ isolation: "isolate" }}
    {...props}
  >
    {children}
  </Stack>
)

/** A sub-component for the container of the hero content */
const HeroContent: FCWC<{}> = ({ children }) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    rowGap={{ xs: 5, md: 10 }}
    height="100%"
    p={3}
    pb={{ xs: 16, md: 32.5 }}
    position="relative"
    overflow="hidden"
  >
    {children}
  </Stack>
)

// A sub-component for the tripIdea hero heading
const TripIdeaHeroHeading: FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => {
  return (
    <Box position="relative" zIndex={5}>
      <Typography
        variant="heading.3xl"
        component="h1"
        color={(theme) => theme.palette["neutral.100"].main}
        textAlign="center"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: { xs: 5, md: 10 },
        }}
      >
        <Typography
          variant="body.lg"
          component="span"
          color={(theme) => theme.palette["neutral.100"].main}
          textAlign="center"
        >
          {subtitle}
        </Typography>
        {title}
      </Typography>
    </Box>
  )
}

/** A sub-component for the section container of the hero content */
const HeroInner: FCWC = ({ children }) => (
  <Stack
    height={{ xs: 570, md: 670 }}
    padding={{ xs: 2, md: 2.5 }}
    sx={{
      isolation: "isolate",
      position: "relative",
    }}
  >
    <Stack
      justifyContent="flex-end"
      height="100%"
      position="relative"
      sx={{ borderRadius: { md: 1.5 } }}
    >
      {children}
    </Stack>
  </Stack>
)
